import Utils from "./Utils";

/**
 * @author Davide Malvezzi
 * @version 0.1.0
 * Google Chrome browser app to allow the use of serial ports comunication inside a web page.
 *	The app acts as an wrapper between the web pages and the serial ports.
 * The app use the chrome.serial API to interact with the serial ports and
 * the chrome.runtime messaging API to exchange information with the web page.
 * It is also provided a simple JavaScript library to use inside the web pages to access the services offered by the app.
 */
/// <reference path="chrome.d.ts"/>

export interface PortInfo {
    portName: string,
    bitrate: number,
    dataBits: "eight",
    parityBit: "even" | "no" | "odd",
    stopBits: "one",
}

export interface ComDevice {
    path:string,
    vendorId?:string,
    productId?:string,
    displayName?:string,
}
export default class SerialPort {
    /**
     * Extension unique id to start the comunication.
     */
    private extensionId = "mbmknndfmjbjohbkbjcogbodmmbppfle";

    /**
     * Port GUID assigned by the app.
     */
    private portGUID?:string;

    /**
     * Contain the unique serial port connection id.
     */
    private serialConnectionId?:number;

    /**
     * Bool that indicates if the serial connection is open.
     */
    private isSerialPortOpen = false;

    /**
     * Callback function to call if there is new data incoming from the serial port connection.
     */
    private onDataReceivedCallback:((data:ArrayBuffer) => void)[] = [];

    /**
     * Callback function to call if there is the connection encountered some problems.
     */
    private onErrorReceivedCallback?:(error:any) => void = undefined;
    private port: chrome.runtime.Port;



    constructor(extensionId?:string) {

        if(extensionId) {
            this.extensionId = extensionId;
        }
        /**
         * Initialize the comunication with the app.
         */
        this.port = chrome.runtime.connect(this.extensionId);
        /**
         * Listener to handle incoming message from the app trought the messaging port.
         * Handled commands are:
         * - guid -> received when the connection with the app is established, represent the GUID assigned to the port
         * - serialdata -> received when new binary data is available on the serial port
         */
        this.port.onMessage.addListener(
            (msg) => {
                Utils.log(msg);
                if(msg.header === "guid"){
                    this.portGUID = msg.guid;
                }
                else if(msg.header === "serialdata"){
                    if(this.onDataReceivedCallback !== undefined){
                        this.onDataReceivedCallback.map(c => c(new Uint8Array(msg.data).buffer));

                    }
                }
                else if(msg.header === "serialerror"){
                    if(this.onErrorReceivedCallback !== undefined) {
                        this.onErrorReceivedCallback(msg.error);
                    }
                }
            }
        );
        this.port.onDisconnect.addListener(function (event) {
            // Happened immediately before adding the proper backend setup.
            // With proper backend setup, allows to determine the extension being disabled or unloaded.
        });

    }


    /**
     * Try to open a serial connection.
     * portInfo MUST contain:
     * portName -> path to the port to open
     * bitrate -> port bit rate as number
     * dataBits -> data bit ("eight" or "seven")
     * parityBit -> parity bit ("no", "odd" or "even")
     * stopBits -> stop bit ("one" or "two")
     * Callback is a function to call to handle the app result.
     */
    public openPort(portInfo:PortInfo, callBack:(response: any) => void){
        chrome.runtime.sendMessage(
            this.extensionId,
            {
                cmd: "open",
                portGUID: this.portGUID,
                info: portInfo
            },
            (response) => {
                if(response.result === "ok"){
                    this.isSerialPortOpen = true;
                    this.serialConnectionId = response.connectionInfo.connectionId;
                }
                callBack(response);
            }
        );
    }

    /**
     * Try to close the serial connection.
     * Callback is a function to call to handle the app result.
     */
    closePort(callBack:any) {
        chrome.runtime.sendMessage(this.extensionId,
            {
                cmd: "close",
                connectionId: this.serialConnectionId
            },
            (response) => {
                if(response.result === "ok"){
                    this.isSerialPortOpen = false;
                }
                callBack(response);
            }
        );
    }


    /**
     *	Write data on the serial port.
     * The request MUST contain:
     * connectionId -> connection unique id provided when the port is opened
     * data -> Array which contains the bytes to send
     */
    write(data:any, callBack?:any){
        chrome.runtime.sendMessage(this.extensionId,
            {
                cmd: "write",
                connectionId: this.serialConnectionId,
                data: Array.prototype.slice.call(new Uint8Array(data))
            },
            (response) => {
                if(response.result === "ok"){
                    if(response.sendInfo.error !== undefined){
                        if(response.sendInfo.error === "disconnected" || response.sendInfo.error === "system_error"){
                            this.isSerialPortOpen = false;
                            this.closePort(function(){});
                        }
                    }
                }
                if(callBack) {
                    callBack(response);
                }
            }
        );
    }
    /**
     * Check if the current port is opened.
     */
    public isOpen() {
        return this.isSerialPortOpen;
    }


    /**
     * Set the new data callback.
     */
    public setOnDataReceivedCallback(callBack:(data:ArrayBuffer) => void){
        this.onDataReceivedCallback.push(callBack);
    }

    /**
     * Set the error callback.
     */
    setOnErrorReceivedCallback(callBack:(error:any) => void) {
        this.onErrorReceivedCallback = callBack;
    }


    /**
     *	Get the list of all serial devices connected to the pc.
     * If there is no error it will return an array of object containing:
     * - path
     * - vendorId (optional)
     * - productId (optional)
     * - displayName (optional)
     * Callback is a function to call to handle the app result.
     */
    getDevicesList(callBack:(list:{result: string, ports: ComDevice[]} )=> void) {
        chrome.runtime.sendMessage(this.extensionId, {cmd: "list"}, callBack);
    }

    /**
     * Used to check if the Serial Interface app is installed on the browser.
     * If it's installed return result: "ok" and the current version
     */
    isExtensionInstalled(callback:(result:boolean, version?:string) => void){
        chrome.runtime.sendMessage(this.extensionId, { cmd: "installed" },
            function (response) {
                if (response && response.result === 'ok'){
                    callback(true, response.version);
                }else{
                    callback(false);
                }
            }
        );
    }

    static available() {
        return typeof chrome !== "undefined" && chrome !== undefined && chrome.runtime !== undefined;
    }
}


